/**
 * Sondeo periódico con retroceso exponencial, pausa en pestaña oculta y aviso
 * por FLANCO (no por contador). Lo usa la sidebar para refrescar la lista de
 * conversaciones; es puro respecto a React para poder probarlo con timers falsos.
 *
 * Por qué no un `setInterval`: un intervalo fijo sigue golpeando un backend caído
 * cada 5 s (deploy, rebuild local) y sondea igual con la pestaña en segundo plano;
 * y avisar "solo en el primer fallo de la racha" con un contador vuelve a llenar
 * la consola cada vez que el backend parpadea.
 */

export const ESPERA_BASE_MS = 5_000;
export const ESPERA_TOPE_MS = 60_000;

/**
 * Espera hasta el siguiente sondeo según los fallos SEGUIDOS acumulados:
 * 0 → base (éxito); 1 → base; 2 → 2·base; 3 → 4·base; ... acotado por `tope`.
 * Con los valores por defecto: 5 → 5 → 10 → 20 → 40 → 60 → 60 s.
 */
export function esperaSondeo(
  fallosSeguidos: number,
  base: number = ESPERA_BASE_MS,
  tope: number = ESPERA_TOPE_MS,
): number {
  const exponente = Math.max(0, Math.floor(fallosSeguidos) - 1);
  return Math.min(base * 2 ** exponente, tope);
}

type DocumentoVisible = Pick<Document, "hidden" | "addEventListener" | "removeEventListener">;

export interface OpcionesSondeo {
  base?: number;
  tope?: number;
  /** Solo en el flanco ok → fallo (el primer fallo de cada racha). */
  onFallo?: (err: unknown) => void;
  /** Solo en el flanco fallo → ok (tras una racha de fallos). */
  onRecuperado?: () => void;
  /** Inyectable para tests; por defecto `document`. */
  documento?: DocumentoVisible;
}

/**
 * Arranca el sondeo: ejecuta `accion` ya, y la reprograma con `esperaSondeo`
 * cuando termina (éxito, rechazo o throw síncrono: los tres cuentan igual). Con
 * la pestaña oculta no se sondea y, al volver a ser visible, se refresca
 * inmediatamente. Nunca hay dos ejecuciones en vuelo a la vez. Devuelve la
 * función que lo detiene; tras llamarla, una ejecución en vuelo no dispara
 * `onFallo` ni `onRecuperado`.
 */
export function iniciarSondeo(
  accion: () => Promise<unknown>,
  opciones: OpcionesSondeo = {},
): () => void {
  const {
    base = ESPERA_BASE_MS,
    tope = ESPERA_TOPE_MS,
    onFallo,
    onRecuperado,
    documento = typeof document !== "undefined" ? document : undefined,
  } = opciones;

  let fallos = 0;
  let fallando = false;
  let enVuelo = false;
  let detenido = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const sondear = () => {
    if (detenido) return;
    clearTimeout(timer);
    timer = undefined;
    // Oculta: no se reprograma; `visibilitychange` lo reanuda.
    if (documento?.hidden) return;
    if (enVuelo) return;
    enVuelo = true;
    // Un throw SINCRONO de `accion` se convierte en rechazo y cae en el `catch`
    // como un fallo mas. Con `accion()` a pelo dejaria `enVuelo=true` para
    // siempre y el sondeo moriria en silencio. Se invoca en el acto (no via
    // `Promise.resolve().then(accion)`) para que "ejecuta ya" siga siendo ya.
    let resultado: Promise<unknown>;
    try {
      resultado = Promise.resolve(accion());
    } catch (err) {
      resultado = Promise.reject(err);
    }
    resultado
      .then(() => {
        // Tras `detener()` nada llega al consumidor: ni exito ni fallo.
        if (detenido) return;
        if (fallando) onRecuperado?.();
        fallando = false;
        fallos = 0;
      })
      .catch((err: unknown) => {
        if (detenido) return;
        fallos++;
        if (!fallando) {
          fallando = true;
          onFallo?.(err);
        }
      })
      .finally(() => {
        enVuelo = false;
        if (!detenido) timer = setTimeout(sondear, esperaSondeo(fallos, base, tope));
      });
  };

  const alCambiarVisibilidad = () => {
    if (!documento?.hidden) sondear();
  };
  documento?.addEventListener("visibilitychange", alCambiarVisibilidad);

  sondear();

  return () => {
    detenido = true;
    clearTimeout(timer);
    documento?.removeEventListener("visibilitychange", alCambiarVisibilidad);
  };
}
